import React, { useEffect, useState } from 'react';
import { Clock, Eye, EyeOff, Type } from 'lucide-react';

/**
 * SlideMetadataPanel
 * ------------------
 * One slide's own details, on the settings page.
 *
 * Every field here already existed in the deck and was already carried by the
 * exporters, the outline and the clipboard — but only two of them could be
 * edited, and only by clicking the header on the slide itself. A duration
 * could not be typed at all: it arrived on decks built elsewhere, showed as a
 * chip in the outline, and there was no way to set one. So this is not a
 * second place to do something; for most of it, it is the first.
 *
 * **Why a picker rather than a list of every slide.** A deck runs to dozens of
 * slides, and a settings page that prints four fields for each of them is a
 * page nobody can find anything on. The picker is the same shape the Slide
 * Background section above it already uses, and it names slides the way the
 * outline does — deck number, then the slide's own name — so the two agree.
 *
 * **Fallbacks are shown, not hidden.** A slide with no name of its own takes
 * its section's title in the header bar, and an empty box here would look like
 * a slide with no title at all. The placeholder says what it will show
 * instead, so clearing a name reads as a deliberate choice rather than a gap.
 */
const SlideMetadataPanel = ({
  slideOptions = [],
  slideNames = {},
  slideSubtitles = {},
  slideDurations = {},
  slideVisibility = {},
  onUpdateSlideName,
  onUpdateSlideSubtitle,
  onUpdateSlideDuration,
  onSetSlideActive,
  /** The app's own "is this a real name, or one generated from the id?" rule. */
  resolveSlideName = (name) => String(name ?? '')
}) => {
  const [selectedSlideId, setSelectedSlideId] = useState('');

  // Follow the deck. A slide deleted or moved out from under its id must not
  // leave this pointing at nothing, and an empty deck that gains its first
  // slide should land on it rather than staying blank.
  useEffect(() => {
    const stillThere = slideOptions.some((slide) => slide.slideId === selectedSlideId);
    if (stillThere) return;
    setSelectedSlideId(slideOptions[0]?.slideId || '');
  }, [slideOptions, selectedSlideId]);

  const selected = slideOptions.find((slide) => slide.slideId === selectedSlideId) || null;

  if (!slideOptions.length) {
    return (
      <p className="mt-3 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-sm text-gray-400">
        This deck has no content slides yet. Add one and its details will appear here.
      </p>
    );
  }

  const slideId = selected?.slideId || '';
  const name = resolveSlideName(slideNames?.[slideId], slideId);
  const subtitle = String(slideSubtitles?.[slideId] || '');
  const duration = slideDurations?.[slideId] ?? '';
  // Absent means included: a deck that has never been touched exports whole.
  const isIncluded = slideVisibility?.[slideId] !== false;

  return (
    <div className="space-y-4 mt-3">
      <div>
        <label htmlFor="slide-metadata-target" className="block text-sm font-semibold text-gray-300 mb-2">
          Slide
        </label>
        <select
          id="slide-metadata-target"
          value={slideId}
          onChange={(event) => setSelectedSlideId(event.target.value)}
          className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {slideOptions.map((slide) => (
            <option key={slide.slideId} value={slide.slideId}>{slide.label}</option>
          ))}
        </select>
        {selected?.sectionTitle && (
          <p className="text-xs text-gray-400 mt-2">
            In <span className="font-semibold text-gray-300">{selected.sectionTitle}</span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="slide-metadata-name" className="block text-sm font-semibold text-gray-300 mb-2">
            Slide title
          </label>
          <input
            id="slide-metadata-name"
            type="text"
            value={name}
            onChange={(event) => onUpdateSlideName?.(slideId, event.target.value)}
            placeholder={selected?.sectionTitle || 'Uses the section title'}
            className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1.5">
            Shown in the slide header and the outline. Left empty, the slide uses its
            section&apos;s title.
          </p>
        </div>

        <div>
          <label htmlFor="slide-metadata-subtitle" className="block text-sm font-semibold text-gray-300 mb-2">
            Slide subtitle
          </label>
          <input
            id="slide-metadata-subtitle"
            type="text"
            value={subtitle}
            onChange={(event) => onUpdateSlideSubtitle?.(slideId, event.target.value)}
            placeholder="Optional line beneath the title"
            className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1.5">
            The quieter second line in the header. Falls back to the section&apos;s
            student-friendly title.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="slide-metadata-duration" className="block text-sm font-semibold text-gray-300 mb-2">
            <Clock size={13} className="inline mr-1.5 -mt-0.5" aria-hidden="true" />
            Planned duration (minutes)
          </label>
          <input
            id="slide-metadata-duration"
            type="number"
            min="0"
            step="1"
            value={duration}
            onChange={(event) => onUpdateSlideDuration?.(slideId, event.target.value)}
            placeholder="Not set"
            className="w-full p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1.5">
            Appears as a chip on the slide in the outline. Clear it, or set it to zero, to
            remove the chip.
          </p>
        </div>

        <div>
          <span className="block text-sm font-semibold text-gray-300 mb-2">Exported files</span>
          <button
            type="button"
            onClick={() => onSetSlideActive?.(slideId, !isIncluded)}
            aria-pressed={isIncluded}
            className={`flex w-full items-center gap-2 rounded-lg border p-3 text-sm font-semibold transition-colors ${
              isIncluded
                ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'border-amber-700 bg-amber-950/40 text-amber-300 hover:bg-amber-950/60'
            }`}
          >
            {isIncluded
              ? <Eye size={15} aria-hidden="true" />
              : <EyeOff size={15} aria-hidden="true" />}
            {isIncluded ? 'Included in exports' : 'Left out of exports'}
          </button>
          <p className="text-xs text-gray-400 mt-1.5">
            A slide left out stays in the deck and keeps its content — the PowerPoint, PDF
            and HTML files simply skip it.
          </p>
        </div>
      </div>

      <p className="flex items-start gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2.5 text-xs text-gray-400">
        <Type size={14} className="mt-0.5 shrink-0 text-blue-400" aria-hidden="true" />
        <span>
          These are the same values the slide&apos;s own header and the outline show, so a
          change here appears there straight away — and travels with the slide when it is
          copied, duplicated or moved to another section.
        </span>
      </p>
    </div>
  );
};

export default SlideMetadataPanel;
